"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import type {
  OperationalAlert,
  OperationalAlertDispatcher,
  OperationalAlertInput,
} from "@/types/operational-alert";
import {
  OPERATIONAL_ALERT_CLEAR_ALL_EVENT,
  OPERATIONAL_ALERT_CLEAR_OPERATION_EVENT,
  OPERATIONAL_ALERT_PUBLISH_EVENT,
} from "@/lib/operational-alert-events";
import { OperationalAlertViewport } from "./operational-alert-viewport";

const OperationalAlertContext = createContext<OperationalAlertDispatcher | null>(null);

function createFingerprint(alert: OperationalAlertInput) {
  return JSON.stringify([
    alert.operationKey,
    alert.severity,
    alert.title,
    alert.description,
    alert.descriptionHighlight,
  ]);
}

export function OperationalAlertProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<OperationalAlert[]>([]);
  const alertsRef = useRef<OperationalAlert[]>([]);
  const idCounter = useRef(0);

  const publishAlert = useCallback((input: OperationalAlertInput) => {
    const fingerprint = createFingerprint(input);
    const duplicate = alertsRef.current.find(
      (alert) => alert.fingerprint === fingerprint,
    );

    if (duplicate) {
      const refreshedAlert: OperationalAlert = {
        ...duplicate,
        ...input,
        revision: duplicate.revision + 1,
      };
      alertsRef.current = [
        refreshedAlert,
        ...alertsRef.current.filter((alert) => alert.id !== duplicate.id),
      ];
      setAlerts(alertsRef.current);
      return duplicate.id;
    }

    idCounter.current += 1;
    const publishedId = `operational-alert-${idCounter.current}`;
    const alert: OperationalAlert = {
      ...input,
      id: publishedId,
      fingerprint,
      revision: 0,
    };
    alertsRef.current = [alert, ...alertsRef.current];
    setAlerts(alertsRef.current);
    return publishedId;
  }, []);

  const dismissAlert = useCallback((id: string) => {
    alertsRef.current = alertsRef.current.filter((alert) => alert.id !== id);
    setAlerts(alertsRef.current);
  }, []);

  const clearAlertsForOperation = useCallback((operationKey: string) => {
    alertsRef.current = alertsRef.current.filter(
      (alert) => alert.operationKey !== operationKey,
    );
    setAlerts(alertsRef.current);
  }, []);

  const clearAllAlerts = useCallback(() => {
    alertsRef.current = [];
    setAlerts([]);
  }, []);

  const dispatcher = useMemo<OperationalAlertDispatcher>(
    () => ({
      publishAlert,
      dismissAlert,
      clearAlertsForOperation,
      clearAllAlerts,
    }),
    [clearAlertsForOperation, clearAllAlerts, dismissAlert, publishAlert],
  );

  useEffect(() => {
    const handlePublish = (event: Event) => {
      publishAlert((event as CustomEvent<OperationalAlertInput>).detail);
    };
    const handleClearOperation = (event: Event) => {
      clearAlertsForOperation((event as CustomEvent<string>).detail);
    };
    const handleClearAll = () => clearAllAlerts();

    window.addEventListener(OPERATIONAL_ALERT_PUBLISH_EVENT, handlePublish);
    window.addEventListener(
      OPERATIONAL_ALERT_CLEAR_OPERATION_EVENT,
      handleClearOperation,
    );
    window.addEventListener(OPERATIONAL_ALERT_CLEAR_ALL_EVENT, handleClearAll);

    return () => {
      window.removeEventListener(OPERATIONAL_ALERT_PUBLISH_EVENT, handlePublish);
      window.removeEventListener(
        OPERATIONAL_ALERT_CLEAR_OPERATION_EVENT,
        handleClearOperation,
      );
      window.removeEventListener(OPERATIONAL_ALERT_CLEAR_ALL_EVENT, handleClearAll);
    };
  }, [clearAlertsForOperation, clearAllAlerts, publishAlert]);

  return (
    <OperationalAlertContext.Provider value={dispatcher}>
      {children}
      <OperationalAlertViewport
        alerts={alerts.slice(0, 4)}
        onDismiss={dismissAlert}
      />
    </OperationalAlertContext.Provider>
  );
}

export function useOperationalAlerts() {
  const dispatcher = useContext(OperationalAlertContext);

  if (!dispatcher) {
    throw new Error(
      "useOperationalAlerts deve ser usado dentro de OperationalAlertProvider.",
    );
  }

  return dispatcher;
}

export const useOperationalAlertDispatcher = useOperationalAlerts;
